import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contacts`, formData);
      setIsSubmitted(true);
      toast.success('Your message has been sent successfully!');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Get In Touch
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Contact Us
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto tracking-wide leading-relaxed">
              Have questions about our services? We're here to help you achieve your sustainability goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-8">
                Let's Start a Conversation
              </h2>
              
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-1">Registered Address</h4>
                    <p className="text-slate-600 dark:text-white/70 tracking-wide leading-relaxed">
                      H.No: 3-119/A/BSH/201, Plot No: 119/A, Mayuri Nagar, Miyapur, Hyderabad, TG, India-500049
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-1">Office Address</h4>
                    <p className="text-slate-600 dark:text-white/70 tracking-wide leading-relaxed">
                      Directorate of Entrepreneurship Innovation Start-Ups (DEIS-JNTUH), 1st Floor, New Admission Building, JNTUH, Kukatpally, Hyderabad – 85
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-1">Phone</h4>
                    <p className="text-slate-600 dark:text-white/70 tracking-wide leading-relaxed">
                    <a href="tel:+918074904918" className="hover:text-emerald-600">+91-8074904918 (Office)</a>
                      <span className="mx-2">|</span>
                      <a href="tel:04045302865" className="hover:text-emerald-600">040-45302865</a>
                      <span className="mx-2">|</span>
                      <a href="tel:+919885884824" className="hover:text-emerald-600">+91-9885884824</a>
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-1">Email</h4>
                    <p className="text-slate-600 dark:text-white/70 tracking-wide leading-relaxed">
                      <a href="mailto:info@renvitek.in" className="hover:text-emerald-600">info@renvitek.in</a>
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-1">Business Hours</h4>
                    <p className="text-slate-600 dark:text-white/70 tracking-wide leading-relaxed">
                      Monday - Saturday: 9:00 AM - 6:00 PM
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-12">
                <h4 className="font-semibold text-emerald-900 dark:text-white mb-4">Our Location</h4>
                <div className="map-container rounded-none overflow-hidden shadow-lg">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3805.30217774561!2d78.39079227516736!3d17.49308288341222!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTfCsDI5JzM1LjEiTiA3OMKwMjMnMzYuMSJF!5e0!3m2!1sen!2sin!4v1771683661142!5m2!1sen!2sin" 
                    width="100%" 
                    height="300" 
                    style={{ border: 0 }}
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="RENVITEK Location"
                    data-testid="contact-map"
                  />
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-slate-50 dark:bg-emerald-900/50 p-8 lg:p-12">
                {isSubmitted ? (
                  <motion.div
                    className="text-center py-12"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-emerald-900 dark:text-white mb-4">
                      Thank You!
                    </h3>
                    <p className="text-slate-600 dark:text-white/70 mb-8 tracking-wide leading-relaxed">
                      Your message has been received. We'll get back to you shortly.
                    </p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      className="bg-emerald-900 hover:bg-emerald-800"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-emerald-900 dark:text-white mb-6">
                      Send Us a Message
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                            Your Name *
                          </label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full"
                            placeholder="John Doe"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                            Email Address *
                          </label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full"
                            placeholder="john@example.com"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                            Phone Number *
                          </label>
                          <Input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full"
                            placeholder="+91 98858 84824"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                            Subject *
                          </label>
                          <Input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                            className="w-full"
                            placeholder="Project Inquiry"
                            data-testid="contact-subject-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-emerald-900 dark:text-white mb-2">
                          Your Message *
                        </label>
                        <Textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          className="w-full"
                          placeholder="Tell us about your project..."
                          data-testid="contact-message-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-emerald-900 hover:bg-emerald-800 text-white py-4 uppercase tracking-wider font-semibold flex items-center justify-center gap-2"
                        data-testid="contact-submit-btn"
                      >
                        <Send size={18} />
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
